import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import {
  DashboardHoWidget,
  DashboardSaleSummary,
} from "../../../types/dashboard.type";
import { Branch, Route } from "../../../types/user.type";
import {
  getAllBranch,
  getAllRoutes,
  getDashboardHoWidgets,
  getDashboardSaleSummary,
  getDashboardWasteBreakdown,
  getDashboardLeftOverBreakdown,
} from "../../../services";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import {
  DashboardPeriod,
  PERIOD_LABEL,
  getPeriodRange,
} from "../../../utils/dashboardPeriod";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/th";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import Widget from "../Widget";
import WidgetSkeleton from "../Skeleton/WidgetsSkeleton";
import OverviewCards from "./OverviewCards";
import SaleSummaryHo from "./SaleSummary";
import ProfitAndCostHo from "./ProfitAndCost";
import BranchSalesChart from "./BranchSalesChart";
import WasteLeftoverSummary from "./WasteLeftoverSummary";
import DashboardReportTable from "./DashboardReportTable";
import SaleReportSubmission from "./SaleReportSubmission";
import { COMPACT_QUERY, useCompact } from "../../../utils/hooks/useCompact";

dayjs.locale("th");

const compact = "@media " + COMPACT_QUERY;

const PERIODS: DashboardPeriod[] = ["today", "this_month", "this_year"];

/** Small inline-labelled filter select (label to the left), per Ref 1. */
function FilterSelect({
  label,
  value,
  onChange,
  children,
  minWidth = 200,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
  minWidth?: number;
}) {
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ whiteSpace: "nowrap" }}
      >
        {label}
      </Typography>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        sx={{
          bgcolor: "#fff",
          minWidth,
          [compact]: { minWidth: minWidth - 40 },
        }}
      >
        {children}
      </Select>
    </Box>
  );
}

export default function DashboardHo() {
  const { isHeadOfficeAccounting } = useUserRole();
  const isCompact = useCompact();
  const navigate = useNavigate();

  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchCode, setBranchCode] = useState<string>("all");
  const [routes, setRoutes] = useState<Route[]>([]);
  const [routeCode, setRouteCode] = useState<string>("all");
  const [anchorDate, setAnchorDate] = useState<Dayjs>(dayjs());
  const [period, setPeriod] = useState<DashboardPeriod>("today");

  const dateRange = useMemo(
    () => getPeriodRange(period, anchorDate),
    [period, anchorDate],
  );

  const totalBranchCount = useMemo(
    () => branches.filter((branch) => branch.branch_code !== "all").length,
    [branches],
  );

  // --- top 3 widgets ---
  const [widgets, setWidgets] = useState<DashboardHoWidget>();
  const [widgetsLoading, setWidgetsLoading] = useState<boolean>(true);

  // --- sales summary (period-bound: donut + cost/profit). The today-only
  //     overview section fetches its own data inside OverviewCards. ---
  const [summary, setSummary] = useState<DashboardSaleSummary>();
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);
  const [summaryError, setSummaryError] = useState<boolean>(false);

  // --- waste + leftover (period-bound: waste-leftover summary card) ---
  const [wasteQty, setWasteQty] = useState<number>(0);
  const [wastePercentDiff, setWastePercentDiff] = useState<number>(0);
  const [leftoverQty, setLeftoverQty] = useState<number>(0);
  const [leftoverPercentDiff, setLeftoverPercentDiff] = useState<number>(0);
  const [wlLoading, setWlLoading] = useState<boolean>(true);
  const [wlError, setWlError] = useState<boolean>(false);

  // Load branches once (HO gets an "all" option prepended).
  useEffect(() => {
    const onGetAllBranch = async () => {
      try {
        const {
          data: { data },
        } = await getAllBranch();
        const list = data ?? [];
        setBranches(
          isHeadOfficeAccounting
            ? [{ uuid: "all", branch_code: "all" } as Branch, ...list]
            : list,
        );
        setBranchCode(
          isHeadOfficeAccounting ? "all" : list?.[0]?.branch_code ?? "all",
        );
      } catch {
        setBranches(
          isHeadOfficeAccounting
            ? [{ uuid: "all", branch_code: "all" } as Branch]
            : [],
        );
      }
    };

    onGetAllBranch();
  }, [isHeadOfficeAccounting]);

  // Load delivery routes (สายส่ง) for the SELECTED branch. Routes are scoped by
  // company, so we resolve the branch's company_id and query by it — the
  // dashboard branch dropdown carries branch_code, not company_id (see the
  // branch_code↔company_id gotcha). "ทุกสาขา" has no single company context, so
  // only the default "ทั้งหมด" option is shown.
  const onGetRoutes = useCallback(async () => {
    if (branchCode === "all") {
      setRoutes([]);
      return;
    }
    const companyId = branches.find(
      (branch) => branch.branch_code === branchCode,
    )?.company_id;
    if (!companyId) {
      setRoutes([]);
      return;
    }
    try {
      const {
        data: { data },
      } = await getAllRoutes(companyId, { includeSpecial: true });
      setRoutes(Array.isArray(data) ? data : data?.rows ?? []);
    } catch {
      setRoutes([]);
    }
  }, [branchCode, branches]);

  useEffect(() => {
    onGetRoutes();
  }, [onGetRoutes]);

  const onGetWidgets = useCallback(async () => {
    setWidgetsLoading(true);
    try {
      const {
        data: { data },
      } = await getDashboardHoWidgets(
        dateRange.date_from,
        dateRange.date_to,
        branchCode,
      );
      setWidgets(data);
    } finally {
      setWidgetsLoading(false);
    }
  }, [dateRange.date_from, dateRange.date_to, branchCode]);

  const onGetSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(false);
    try {
      const {
        data: { data },
      } = await getDashboardSaleSummary(
        dateRange.date_from,
        dateRange.date_to,
        branchCode,
      );
      setSummary(data);
    } catch {
      setSummaryError(true);
      setSummary(undefined);
    } finally {
      setSummaryLoading(false);
    }
  }, [dateRange.date_from, dateRange.date_to, branchCode]);

  const onGetWasteLeftover = useCallback(async () => {
    setWlLoading(true);
    setWlError(false);
    try {
      const [wasteRes, leftoverRes] = await Promise.all([
        getDashboardWasteBreakdown(
          dateRange.date_from,
          dateRange.date_to,
          dateRange.period_type,
          branchCode,
        ),
        getDashboardLeftOverBreakdown(
          dateRange.date_from,
          dateRange.date_to,
          dateRange.period_type,
          branchCode,
        ),
      ]);

      const waste = wasteRes.data.data;
      const leftover = leftoverRes.data.data;

      setWasteQty(waste?.current_waste_qty ?? 0);
      setWastePercentDiff(waste?.percent_waste_qty_diff ?? 0);
      setLeftoverQty(leftover?.current_leftover_qty ?? 0);
      setLeftoverPercentDiff(leftover?.percent_leftover_qty_diff ?? 0);
    } catch {
      setWlError(true);
    } finally {
      setWlLoading(false);
    }
  }, [dateRange.date_from, dateRange.date_to, dateRange.period_type, branchCode]);

  useEffect(() => {
    onGetWidgets();
  }, [onGetWidgets]);

  useEffect(() => {
    onGetSummary();
  }, [onGetSummary]);

  useEffect(() => {
    onGetWasteLeftover();
  }, [onGetWasteLeftover]);

  const renderDatePicker = () => {
    const common = {
      value: anchorDate,
      onChange: (value: Dayjs | null) => {
        if (value) setAnchorDate(value);
      },
      slotProps: {
        textField: { size: "small" as const, fullWidth: true },
      },
      sx: { bgcolor: "#fff" },
    };

    if (period === "this_month") {
      return (
        <DatePicker {...common} views={["year", "month"]} format="MM/YYYY" />
      );
    }
    if (period === "this_year") {
      return <DatePicker {...common} views={["year"]} format="YYYY" />;
    }
    return <DatePicker {...common} format="DD/MM/YYYY" />;
  };

  return (
    <Box sx={{ padding: "8px 0" }}>
      {/* Top bar: title + branch (สาขา) + delivery-route (สายส่ง) filters */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <Typography variant="h5" fontWeight={600}>
          ภาพรวมระบบ
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FilterSelect
            label="สาขา"
            value={branchCode}
            onChange={(code) => {
              setBranchCode(code);
              setRouteCode("all");
            }}
            minWidth={240}
          >
            {branches.map((branch) => (
              <MenuItem
                key={branch.uuid}
                value={branch.branch_code}
                sx={{ textTransform: "capitalize" }}
              >
                {branch.branch_code !== "all"
                  ? `${branch.branch_code} ${branch.branch_name}`
                  : "สาขาทั้งหมด"}
              </MenuItem>
            ))}
          </FilterSelect>
          {/* สายส่ง: filter UI per design. Dashboard endpoints currently scope by
              branch only — route-level filtering is a backend gap (not yet plumbed). */}
          <FilterSelect
            label="สายส่ง"
            value={routeCode}
            onChange={setRouteCode}
            minWidth={200}
          >
            <MenuItem value="all">ทั้งหมด</MenuItem>
            {routes.map((route) => (
              <MenuItem key={route.uuid} value={route.route_code}>
                {route.route_code} {route.route_name}
              </MenuItem>
            ))}
          </FilterSelect>
        </Box>
      </Box>

      <Grid container spacing={isCompact ? 1.5 : 3}>
        {/* 1. Top 3 widgets */}
        {widgetsLoading && (
          <WidgetSkeleton length={3} size={{ xs: 12, sm: 4 }} />
        )}
        {!widgetsLoading && (
          <>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Widget
                title="จำนวนเอกสารที่รอตรวจสอบ"
                total={widgets?.submitted_report_count ?? 0}
                icon={<RequestQuoteOutlinedIcon />}
                type="white"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Widget
                title="จำนวนสาขาที่ส่งรายงาน"
                total={widgets?.submitted_branch_count ?? 0}
                denominator={totalBranchCount}
                icon={<HistoryOutlinedIcon />}
                type="primary"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Widget
                title="รายงานยอดขายที่รอแก้ไข"
                total={widgets?.request_change_report_count ?? 0}
                icon={<BorderColorOutlinedIcon />}
                type="link"
                onClick={() => navigate("/report/sale")}
              />
            </Grid>
          </>
        )}

        {/* 2. Overview section — static "ภาพรวมวันนี้" (today-only, branch-scoped;
            self-contained, NOT bound to the รายวัน/รายเดือน/รายปี selector). */}
        <Grid size={12}>
          <OverviewCards branchCode={branchCode} />
        </Grid>

        {/* 3. ยอดรายสาขา — per-branch chart, only for "ทุกสาขา", not period-bound */}
        {branchCode === "all" && (
          <Grid size={12}>
            <BranchSalesChart />
          </Grid>
        )}

        {/* Global period control + period-aware date picker (header of section 4) */}
        <Grid size={12}>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <RadioGroup
              row
              value={period}
              onChange={(_, value) => setPeriod(value as DashboardPeriod)}
              sx={{
                gap: 1,
                "& .MuiFormControlLabel-root": { mr: 1 },
              }}
            >
              {PERIODS.map((p) => (
                <FormControlLabel
                  key={p}
                  value={p}
                  control={<Radio size="small" />}
                  label={PERIOD_LABEL[p]}
                />
              ))}
            </RadioGroup>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
              <Box sx={{ minWidth: 200 }}>{renderDatePicker()}</Box>
            </LocalizationProvider>
          </Box>
        </Grid>

        {/* 4. 3-column row: donut | cost/profit | waste-leftover summary */}
        <Grid size={{ xs: 12, md: 4 }}>
          <SaleSummaryHo
            summary={summary}
            loading={summaryLoading}
            error={summaryError}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <ProfitAndCostHo
            summary={summary}
            loading={summaryLoading}
            error={summaryError}
            period={period}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <WasteLeftoverSummary
            wasteQty={wasteQty}
            wastePercentDiff={wastePercentDiff}
            leftoverQty={leftoverQty}
            leftoverPercentDiff={leftoverPercentDiff}
            loading={wlLoading}
            error={wlError}
            period={period}
          />
        </Grid>

        {/* 5. Period sales graph (month/year) + branch summary table */}
        <Grid size={12}>
          <DashboardReportTable
            dateRange={dateRange}
            branchCode={branchCode}
            period={period}
          />
        </Grid>

        {/* 6. Report submission table */}
        <Grid size={12}>
          <SaleReportSubmission
            dateRange={dateRange}
            branchCode={branchCode}
            period={period}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
